import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import { Container, SectionHeading, PrimaryLink, SecondaryLink } from "@/components/ui-kit";
import PlanCard from "@/components/PlanCard";
import { getCombinedOwnershipTotal, getPlanPrice, PLANS } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";

export default function Plans() {
  const { isPH, link } = useRegion();
  const [catalogTab, setCatalogTab] = useState("normal");
  const ownership = getPlanPrice("ownership", "ph");
  const launch = getPlanPrice("assisted_setup_launch", "ph");
  const maintenance = getPlanPrice("maintenance", "ph");
  const responsibility = getPlanPrice("responsibility", "ph");
  const priority = getPlanPrice("priority", "ph");
  const combined = getCombinedOwnershipTotal("ph");
  return (
    <div data-testid="plans-page">
      {/* HEADER */}
      <section className="bg-[#0F172A]">
        <Container className="py-12 md:py-16 text-center">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-3">
            {isPH ? "Responsibility Options — Philippines" : "Responsibility Options"}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-white text-balance max-w-3xl mx-auto">
            Choose how much responsibility you want to keep — or hand it over
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
            {isPH
              ? "Peso pricing for Philippine businesses. This is not about features — it's about who is responsible when something goes wrong."
              : "This is not about features. It's about who is responsible when something goes wrong."}
          </p>
        </Container>
      </section>

      {isPH && (
        <section className="border-b border-emerald-100 bg-emerald-50 py-8" data-testid="ph-pricing-summary">
          <Container>
            <h2 className="font-display text-lg font-bold text-[#0F172A]">Philippines / PHP pricing</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-[#334155] sm:grid-cols-2 lg:grid-cols-3">
              <p data-testid="ph-price-ownership"><strong>Website Ownership</strong> — {ownership.price} {ownership.period}</p>
              <p data-testid="ph-price-launch"><strong>Assisted Setup &amp; Launch</strong> — {launch.price} {launch.period}</p>
              <p data-testid="ph-price-maintenance"><strong>Maintenance Only</strong> — {maintenance.price}{maintenance.period.replace("/ ", "/")}</p>
              <p data-testid="ph-price-responsibility"><strong>Responsibility Plan</strong> — {responsibility.price}{responsibility.period.replace("/ ", "/")}</p>
              <p data-testid="ph-price-priority"><strong>Priority &amp; Reputation Care</strong> — {priority.price}{priority.period.replace("/ ", "/")}</p>
              <p data-testid="ph-price-ownership-launch-total"><strong>Ownership + Assisted Setup &amp; Launch</strong> — {combined.price} total</p>
            </div>
          </Container>
        </section>
      )}

      <section className="border-b bg-white" data-testid="plans-catalog-tabs"><Container className="flex gap-6 py-4"><button type="button" onClick={() => setCatalogTab("normal")} className={catalogTab === "normal" ? "font-semibold text-emerald-700" : "text-slate-600"} data-testid="normal-plans-tab">Normal Plans</button><button type="button" onClick={() => setCatalogTab("test")} className={catalogTab === "test" ? "font-semibold text-emerald-700" : "text-slate-600"} data-testid="test-plans-tab">Test Plans</button></Container></section>
      {/* PLANS GRID */}
      <section className="py-20 md:py-24 bg-[#F8FAFC]">
        <Container>
          {catalogTab === "normal" ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-7">{PLANS.map((p) => (
              <PlanCard key={p.id} plan={p} />
            ))}</div> : <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="test-plans-catalog">{[
              ["test-handover", "₱50 one-time", "Test Handover", "Same handover/ownership lifecycle as the normal one-time plan. PayMongo sandbox Hosted Checkout."],
              ["test-basic", "₱10/month", "Test Basic", "Same entitlement mapping as Basic. Subscription activation pending PayMongo capability."],
              ["test-responsibility", "₱20/month", "Test Responsibility", "Same entitlement mapping as Responsibility. Subscription activation pending PayMongo capability."],
              ["test-priority", "₱30/month", "Test Priority", "Same entitlement mapping as Priority. Subscription activation pending PayMongo capability."],
            ].map(([id, price, name, detail]) => <article key={id} className="border border-slate-200 bg-white p-6" data-testid={`test-plan-${id}`}><p className="text-2xl font-bold text-slate-950">{price}</p><h2 className="mt-2 text-lg font-semibold">{name}</h2><p className="mt-2 text-sm text-slate-600">{detail}</p><a href={id === "test-handover" ? "/ph/checkout?plan=test-handover&market=ph" : link("/account/login")} className="mt-5 inline-block font-semibold text-emerald-700" data-testid={`test-plan-${id}-checkout-link`}>{id === "test-handover" ? "Continue to sandbox checkout" : "Subscription activation pending"}</a></article>)}</div>}

          <p className="mt-10 text-center text-sm text-[#94A3B8] max-w-2xl mx-auto">
            {isPH
              ? "Prices are fixed Philippine market prices for the standard scope described. Applicable taxes, if any, are shown where required. Custom work is quoted separately. "
              : "Prices are fixed EU / international market prices. VAT, reverse-charge treatment, or other applicable tax treatment depends on the client and transaction and is shown where required. "}
            Monthly payments do not automatically create ownership rights. See our <a href={link("/clear-terms")} className="text-[#10B981] hover:underline">Payment Terms</a> and{" "}
            <a href={link("/terms-and-conditions")} className="text-[#10B981] hover:underline">Terms &amp; Conditions</a> for full details.
          </p>
        </Container>
      </section>

      {/* HELP / CTA */}
      <section className="py-20 md:py-24 bg-white">
        <Container>
          <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-10 md:p-14 text-center">
            <div className="inline-flex rounded-2xl bg-[#10B981]/10 p-4 mb-5">
              <HelpCircle size={28} className="text-[#10B981]" />
            </div>
            <h2 className="text-3xl font-bold font-display text-[#0F172A] text-balance">
              Not sure which plan is right for you?
            </h2>
            <p className="mt-4 text-lg text-[#475569] max-w-2xl mx-auto">
              Let's talk. We'll confirm what you have, what you need, and which option fits — with no pressure and no
              sales scripts.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryLink to={link("/contact")} testId="plans-cta-contact">
                Request a Free Website Check
              </PrimaryLink>
              <SecondaryLink to={link("/faq")} testId="plans-cta-faq">
                Read the FAQ
              </SecondaryLink>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
